import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileDown, FileText, Send, RefreshCw, Loader2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-client";
import {
  useTaxDocument,
  useSubmitToSii,
  StatusBadge,
  StatusTimeline,
  SubmitToSiiDialog,
  DTE_TYPE_LABELS,
} from "@/features/tax-documents";
import type { DteTypeCode } from "@/features/tax-documents";

function formatCurrency(value: string | number | null): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-CL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function DetalleDocumentoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { document: taxDoc, isLoading, error, refetch } = useTaxDocument(id);
  const { queryStatus, isQuerying } = useSubmitToSii();
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  // Redirect to edit page if viewing a draft
  useEffect(() => {
    if (taxDoc?.status === "draft" && id) {
      navigate(`/facturacion/emision/nuevo?draftId=${id}`, { replace: true });
    }
  }, [taxDoc?.status, id, navigate]);

  const handleDownloadXml = async () => {
    if (!id) return;
    try {
      const blob = await apiFetch.blob(`/api/tax-documents/${id}/xml`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dte-${taxDoc?.folio ?? id}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error("Error al descargar el XML");
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      const blob = await apiFetch.blob(`/api/tax-documents/${id}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dte-${taxDoc?.folio ?? id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error("Error al generar el PDF");
    }
  };

  const handleQueryStatus = async () => {
    if (!id) return;
    try {
      await queryStatus(id);
      await refetch();
      toast.success("Estado actualizado");
    } catch (err) {
      toast.error("Error al consultar estado");
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Cargando documento...
      </div>
    );
  }

  if (error || !taxDoc) {
    return (
      <div className="p-6">
        <div className="text-red-500">
          {error?.message ?? "Documento no encontrado"}
        </div>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/facturacion/emision")}
        >
          Volver
        </Button>
      </div>
    );
  }

  const docTypeLabel =
    DTE_TYPE_LABELS[Number(taxDoc.documentTypeCode) as DteTypeCode] ??
    taxDoc.documentType;

  const totals = taxDoc.countryMetadata?.totals;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/facturacion/emision")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{docTypeLabel}</h1>
            <StatusBadge status={taxDoc.status} />
          </div>
          <p className="text-muted-foreground">
            Folio: {taxDoc.folio} • {formatDate(taxDoc.issueDate)}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button variant="outline" onClick={handleDownloadPdf}>
          <FileText className="mr-2 h-4 w-4" />
          Descargar PDF
        </Button>
        <Button variant="outline" onClick={handleDownloadXml}>
          <FileDown className="mr-2 h-4 w-4" />
          Descargar XML
        </Button>
        {taxDoc.status === "pending" && (
          <Button onClick={() => setSubmitDialogOpen(true)}>
            <Send className="mr-2 h-4 w-4" />
            Enviar a SII
          </Button>
        )}
        {/* Show "Consultar Estado" button when:
            - Document is submitted (waiting for SII response)
            - Document is accepted by SII but receptor status is still pending
        */}
        {(taxDoc.status === "submitted" ||
          (taxDoc.status === "accepted" &&
            taxDoc.receptorStatus === "pending")) && (
          <Button
            variant="outline"
            onClick={handleQueryStatus}
            disabled={isQuerying}
          >
            {isQuerying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Consultar Estado
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Parties */}
          <div className="bg-card border rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Emisor */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Emisor
                </h3>
                <div className="text-sm">
                  <div className="font-medium">TALOS LABS SPA</div>
                  <div className="text-muted-foreground">77.938.104-8</div>
                </div>
              </div>

              {/* Receptor */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Receptor
                </h3>
                <div className="text-sm">
                  <div className="font-medium">
                    {taxDoc.recipientName ?? "-"}
                  </div>
                  <div className="text-muted-foreground">
                    {taxDoc.recipientTaxId}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-card border rounded-lg p-6">
            <h3 className="text-sm font-medium mb-4">Resumen</h3>
            <div className="space-y-2 text-sm">
              {totals?.montoNeto && totals.montoNeto > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Neto:</span>
                  <span>{formatCurrency(totals.montoNeto)}</span>
                </div>
              )}
              {totals?.montoExento && totals.montoExento > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Exento:</span>
                  <span>{formatCurrency(totals.montoExento)}</span>
                </div>
              )}
              {totals?.iva && totals.iva > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">IVA 19%:</span>
                  <span>{formatCurrency(totals.iva)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t font-medium">
                <span>Total:</span>
                <span className="text-lg">
                  {formatCurrency(taxDoc.totalAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Track ID */}
          {taxDoc.trackingId && (
            <div className="bg-card border rounded-lg p-6">
              <h3 className="text-sm font-medium mb-2">Track ID SII</h3>
              <code className="text-sm bg-muted px-2 py-1 rounded">
                {taxDoc.trackingId}
              </code>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Timeline */}
          <div className="bg-card border rounded-lg p-6">
            <StatusTimeline
              currentStatus={taxDoc.status}
              createdAt={taxDoc.createdAt}
              submittedAt={taxDoc.submittedAt}
              acceptedAt={taxDoc.acceptedAt}
              statusDetail={taxDoc.statusDetail}
              authorityStatusCode={taxDoc.authorityStatusCode}
              receptorStatus={taxDoc.receptorStatus}
              receptorActionAt={taxDoc.receptorActionAt}
            />
          </div>
        </div>
      </div>

      {/* Submit Dialog */}
      <SubmitToSiiDialog
        open={submitDialogOpen}
        onOpenChange={setSubmitDialogOpen}
        documents={[
          {
            id: taxDoc.id,
            documentType: taxDoc.documentType,
            documentTypeCode: taxDoc.documentTypeCode,
            folio: taxDoc.folio,
            issueDate: taxDoc.issueDate,
            recipientTaxId: taxDoc.recipientTaxId,
            recipientName: taxDoc.recipientName,
            netAmount: taxDoc.netAmount,
            taxAmount: taxDoc.taxAmount,
            totalAmount: taxDoc.totalAmount,
            status: taxDoc.status,
            receptorStatus: taxDoc.receptorStatus,
            createdAt: taxDoc.createdAt,
          },
        ]}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
